import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "../api/axios";
import "./styles/OrderSuccess.css";

import { getImageUrl } from "../utility/imageHelper";
import { slugify } from "../utility/slugify";

import SEO from "../seo/SEO";
import config from "../config/config";

const OrderDetails = () => {
  const { orderUuid } = useParams();
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier] = useState("");
  const [orderFound, setOrderFound] = useState(true);
  const [orderStatus, setOrderStatus] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderCreatedAt, setOrderCreatedAt] = useState(null);

  // Fetch the order details
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`/api/orders/verify?order_uuid=${orderUuid}`);

        if (res.data.order) {
          const order = res.data.order;

          if (order.tracking_number) setTrackingNumber(order.tracking_number);
          if (order.carrier) setCarrier(order.carrier.toUpperCase());
          if (order.status) setOrderStatus(order.status.toLowerCase());
          if (order.created_at) setOrderCreatedAt(new Date(order.created_at));
          if (order.total_price) setTotalPrice(parseFloat(order.total_price));
          if (order.products) setProducts(order.products);
        } else {
          setOrderFound(false);
        }
      } catch (error) {
        console.error("🔥 Failed to fetch order:", error);
        setOrderFound(false);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderUuid]);

  // Loading state
  if (loading) {
    return (
      <div className="order-success"><div className="loader-wrapper-global"><div className="spinner"></div></div></div>
    );
  }

  // Not found
  if (!orderFound) {
    return (
      <div className="order-success">
        <h2>❌ Order Not Found</h2>
        <p>No order matches ID: {orderUuid}</p>
        <Link to="/shop/all-products" className="continue-shopping-btn">
          Back to Shop
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEO
        robots="noindex, nofollow"
        title="Order Details - Withrow Knives"
        description={`View details for your order ${orderUuid}.`}
        keywords={[
          "order details",
          "order tracking",
          "Withrow Knives order",
        ]}
        url={`${config.metadata.siteUrl}/user/order/${orderUuid}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Order",
          "name": "Order Details - Withrow Knives",
          "url": `${config.metadata.siteUrl}/user/order/${orderUuid}`,
          "description": `Order details for order ID: ${orderUuid}.`,
          "orderNumber": orderUuid,
          "orderStatus": trackingNumber ? "OrderShipped" : "OrderProcessing",
        }}
      />

      <div className="order-success">
        <h2>Order Details</h2>

        {/* Status Message */}
        <p>
          {orderStatus === "shipped" && "Your order has been shipped and is on its way!"}
          {orderStatus === "cancelled" && "This order has been cancelled."}
          {orderStatus === "complete" && "This order has been completed."}
          {["pending", "paid"].includes(orderStatus) &&
            "Your order has been confirmed and is being processed. Your item(s) should ship soon."}
        </p>

        <p>
          <strong>Order ID:</strong> {orderUuid}
        </p>

        {/* Tracking Info */}
        {trackingNumber && carrier && (
          <p>
            <strong>Tracking:</strong>{" "}
            <a
              href={
                carrier === "USPS"
                  ? `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}`
                  : carrier === "UPS"
                  ? `https://www.ups.com/track?tracknum=${trackingNumber}`
                  : carrier === "FEDEX"
                  ? `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
                  : "/"
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              Track Your Package ({carrier})
            </a>
          </p>
        )}

        <hr />

        <h3>Order Summary</h3>
        <p>
          <strong>Total Price:</strong> ${totalPrice.toFixed(2)}
        </p>

        {orderCreatedAt && (
          <p>
            <strong>Date Placed:</strong>{" "}
            {orderCreatedAt.toLocaleString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        )}

        {/* Products */}
        {products.length > 0 && (
          <div className="order-products">
            <ul>
              {products.map((product) => (
                <Link
                  className="orders-view-product-page"
                  to={`/shop-knives/knife/${product.product_id}/${slugify(
                    product.name
                  )}`}
                  key={product.product_id}
                >
                  <li className="order-product">
                    <img
                      src={getImageUrl(product.image_url)}
                      alt={product.name}
                      className="order-product-img"
                    />
                    <div className="order-product-details">
                      <span className="product-name">{product.name} (#{product.product_id})</span>
                      <span className="product-qty">Qty: {product.quantity}</span>
                      <span className="product-price">
                        ${product.price.toFixed(2)}
                      </span>
                    </div>
                  </li>
                </Link>
              ))}
            </ul>
          </div>
        )}

        <Link
          to="/shop-knives/all-knives"
          className="continue-shopping-btn"
        >
          Continue Shopping
        </Link>
      </div>
    </>
  );
};

export default OrderDetails;
